import type {
  APIBodyParamsV2,
  ApiContext,
  ApiDashboardElement,
  ApiDashboardLayoutParametersType,
  ApiDashboardName,
  ApiReturnCampaign,
  ApiReturnDashboard,
  ApiReturnFrontendPermission,
  ApiReturnMeasuresValidated,
  ApiReturnMeasureType,
  ApiReturnMfe,
  ApiReturnModel,
  ApiReturnProject,
  ApiReturnResourcesTags,
  ApiReturnSite,
  ApiReturnSiteIdsToDashboardIds,
  ApiReturnTag,
  ApiSourceLabelPair,
  ApiSourceLabelsRequest,
  ApiSourceLabelsResponse,
  DashboardPublicLinkContext,
  IntervalJSON,
  SourceLabelScope
} from '@highimpact-frontend/api-types';

import type {
  ApiResourceInput,
  ApiReturnResourceResult,
  ApiTagInput,
  DashboardElementResourceLink,
  DashboardHostApi,
  ReportBuildPayload,
  ReportModelStructureResult,
  ResourceEntityQuery,
} from './DashboardHostApi';

// -- host service shapes (duck-typed; host passes concrete service instances) --

export interface HostDashboardService {
  getSiteIdsToDashboardIds(): Promise<ApiReturnSiteIdsToDashboardIds>;
  getDashboard(id: number): Promise<ApiReturnDashboard>;
  getDashboardElement(id: number): Promise<ApiDashboardElement>;
  postDashboard(params: ApiReturnDashboard): Promise<ApiReturnDashboard>;
  createPublicDashboard(
    id: number,
    context: DashboardPublicLinkContext
  ): Promise<string>;
  renameDashboard(
    id: number,
    data: ApiDashboardName
  ): Promise<ApiReturnDashboard>;
  renameWidget(
    dashboardId: number,
    widgetElementId: number,
    data: ApiDashboardName
  ): Promise<ApiReturnDashboard>;
  deleteDashboard(id: number): Promise<string>;
  deleteDashboardWidget(id: number): Promise<string>;
  putDashboardLayout(
    dashboardId: number,
    data: ApiDashboardLayoutParametersType
  ): Promise<string>;
  postDashboardElement(
    dashboardId: number,
    data: ApiDashboardElement
  ): Promise<number>;
  putDashboardElement(
    elementId: number,
    data: ApiDashboardElement
  ): Promise<ApiDashboardElement>;
}

export interface HostResourceService {
  add(resource: ApiResourceInput): Promise<ApiReturnResourceResult>;
  update(
    resourceId: number,
    resource: Partial<ApiResourceInput>
  ): Promise<ApiReturnResourceResult>;
  delete(resourceId: number): Promise<ApiReturnResourceResult>;
  get(resourceIds: number[]): Promise<ApiReturnResourceResult[]>;
  getByEntity(params: ResourceEntityQuery): Promise<ApiReturnResourceResult[]>;
}

export interface HostTagService {
  add(params: ApiTagInput): Promise<ApiReturnTag>;
  getAll(): Promise<ApiReturnTag[]>;
  getAllFromProject(projectId: number): Promise<ApiReturnTag[]>;
  getAllResourcesTags(): Promise<ApiReturnResourcesTags[]>;
  associateTagToProject(
    tags: string[],
    projectId: number
  ): Promise<ApiReturnTag>;
  associateTagToResource(
    resourceId: number,
    tags: string[]
  ): Promise<ApiReturnTag>;
  deleteFromResource(
    resourceId: number,
    tags: string[]
  ): Promise<ApiReturnResourceResult>;
}

export interface HostSiteCatalogService {
  getAll(): Promise<ApiReturnSite[]>;
}

export interface HostProjectCatalogService {
  getAll(): Promise<ApiReturnProject[]>;
}

export interface HostDashboardElementResourceService {
  add(
    link: DashboardElementResourceLink
  ): Promise<DashboardElementResourceLink>;
  delete(
    link: DashboardElementResourceLink
  ): Promise<DashboardElementResourceLink>;
}

export interface HostReportModelService {
  getReportModelsBySite(siteId: number): Promise<ApiReturnModel[]>;
  getReportModelStructure(
    projectId: number,
    siteId: number,
    reportModelId: number,
    campaignId?: number | null,
    intervals?: IntervalJSON[] | null
  ): Promise<ReportModelStructureResult>;
}

export interface HostMfeService {
  getMfes(): Promise<ApiReturnMfe[]>;
}

export interface HostSourceLabelService {
  postLabels(request: ApiSourceLabelsRequest): Promise<ApiSourceLabelsResponse>;
}

export interface HostPermissionsService {
  fetchFrontendPermission(
    params: ApiContext,
    signal: AbortSignal
  ): Promise<ApiReturnFrontendPermission>;
}

export interface HostCampaignCatalogService {
  getBySite(siteId: number): Promise<ApiReturnCampaign[]>;
}

export interface HostMeasuresService {
  query(
    body: APIBodyParamsV2,
    signal?: AbortSignal
  ): Promise<ApiReturnMeasuresValidated>;
}

export interface HostMeasureTypesCatalogService {
  getAll(): Promise<ApiReturnMeasureType[]>;
}

export interface HostServicesInput {
  readonly dashboard: HostDashboardService;
  readonly mfe: HostMfeService;
  readonly resource: HostResourceService;
  readonly project: HostProjectCatalogService;
  readonly site: HostSiteCatalogService;
  readonly tag: HostTagService;
  readonly sources: HostSourceLabelService;
  readonly dashboardElementResource: HostDashboardElementResourceService;
  readonly reportModel: HostReportModelService;
  readonly permissions: HostPermissionsService;
  readonly campaign: HostCampaignCatalogService;
  readonly measures: HostMeasuresService;
  readonly measureTypes: HostMeasureTypesCatalogService;
  readonly buildReportDocx: (
    reportStructure: ReportBuildPayload
  ) => Promise<Blob>;
}

// -- factory --

/**
 * Maps host app service instances into a DashboardHostApi object.
 * Keeps HTTP and axios at the host edge; the library only sees this interface.
 */
export const createDashboardHostApi = (
  services: HostServicesInput
): DashboardHostApi => ({
  dashboard: {
    getSiteIdsToDashboardIds: () =>
      services.dashboard.getSiteIdsToDashboardIds(),
    getDashboard: (id) => services.dashboard.getDashboard(id),
    getDashboardElement: (id) => services.dashboard.getDashboardElement(id),
    postDashboard: (params) => services.dashboard.postDashboard(params),
    createPublicDashboard: (id, context) =>
      services.dashboard.createPublicDashboard(id, context),
    renameDashboard: (id, data) =>
      services.dashboard.renameDashboard(id, data),
    renameWidget: (dashboardId, widgetElementId, data) =>
      services.dashboard.renameWidget(dashboardId, widgetElementId, data),
    deleteDashboard: (id) => services.dashboard.deleteDashboard(id),
    deleteDashboardWidget: (id) => services.dashboard.deleteDashboardWidget(id),
    putDashboardLayout: (dashboardId, data) =>
      services.dashboard.putDashboardLayout(dashboardId, data),
    postDashboardElement: (dashboardId, data) =>
      services.dashboard.postDashboardElement(dashboardId, data),
    putDashboardElement: (elementId, data) =>
      services.dashboard.putDashboardElement(elementId, data),
  },
  mfe: {
    getMfes: () => services.mfe.getMfes(),
  },
  report: {
    buildReportDocx: (reportStructure) =>
      services.buildReportDocx(reportStructure),
    getReportModelsBySite: (siteId) =>
      services.reportModel.getReportModelsBySite(siteId),
    getReportModelStructure: (
      projectId,
      siteId,
      reportModelId,
      options
    ): Promise<ReportModelStructureResult> =>
      services.reportModel.getReportModelStructure(
        projectId,
        siteId,
        reportModelId,
        options?.campaignId ?? null,
        options?.intervals ?? null
      ),
  },
  resource: {
    add: (resource) => services.resource.add(resource),
    update: (resourceId, resource) =>
      services.resource.update(resourceId, resource),
    delete: (resourceId) => services.resource.delete(resourceId),
    get: (resourceIds) => services.resource.get(resourceIds),
    getByEntity: (params) => services.resource.getByEntity(params),
  },
  site: {
    getAll: () => services.site.getAll(),
  },
  project: {
    getAll: () => services.project.getAll(),
  },
  tag: {
    add: (params) => services.tag.add(params),
    getAll: () => services.tag.getAll(),
    getAllFromProject: (projectId) =>
      services.tag.getAllFromProject(projectId),
    getAllResourcesTags: () => services.tag.getAllResourcesTags(),
    associateTagToProject: (tags, projectId) =>
      services.tag.associateTagToProject(tags, projectId),
    associateTagToResource: (resourceId, tags) =>
      services.tag.associateTagToResource(resourceId, tags),
    deleteFromResource: (resourceId, tags) =>
      services.tag.deleteFromResource(resourceId, tags),
  },
  sources: {
    getLabels: (
      pairs: readonly ApiSourceLabelPair[],
      scope?: SourceLabelScope
    ): Promise<NonNullable<ApiSourceLabelsResponse['sources']>> =>
      services.sources
        .postLabels({
          pairs: [...pairs],
          project_id: scope?.project_id,
          site_id: scope?.site_id,
        })
        .then((response) => response.sources ?? {}),
  },
  dashboardElementResource: {
    add: (link) => services.dashboardElementResource.add(link),
    delete: (link) => services.dashboardElementResource.delete(link),
  },
  permissions: {
    fetchFrontendPermission: (params, signal) =>
      services.permissions.fetchFrontendPermission(params, signal),
  },
  campaign: {
    getBySite: (siteId) => services.campaign.getBySite(siteId),
  },
  measures: {
    query: (body, signal) => services.measures.query(body, signal),
  },
  measureTypes: {
    getAll: () => services.measureTypes.getAll(),
  },
});

/** Type guard helper: checks that a report model result has the expected shape. */
export const isReportModelStructureResult = (
  value: unknown
): value is ReportModelStructureResult => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['report_structure'] === 'object' &&
    Array.isArray(candidate['layouts'])
  );
};
